'use client';
import { usePathname, useRouter } from 'next/navigation';
import { Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { routes } from '@lib/constants/routes-constant';
import { domains } from '@lib/constants/domains';
import { MainLogo } from '@modules/common/components/logo/main-logo';
import useSearchHandle from '@lib/hooks/use-search-handle';
import objectToURLSearchParams from '@lib/util/object-to-url-search-params';
import SearchIcon from '@modules/common/icons/search';
import CartDropdown from '@modules/layout/components/cart-dropdown';
import clsx from 'clsx';
import './header.scss';
import Link from 'next/link';
import { ErrorMessage } from '@modules/common/components/error-message';
import { useStore } from '@lib/context/store-context';
import { SearchLocationInput } from '@modules/common/components/search-location-input/search-location-input';
import { Menu, Transition } from '@headlessui/react';
import User from '@modules/common/icons/user';
import { LOGIN_VIEW, useAccount } from '@lib/context/account-context';
import { CartDropdownContext } from '@lib/context/cart-dropdown-context';
import { getLogger } from '@lib/util/logging-util';

const logger = getLogger();
import { CARD_PROGRAM_DISPLAY, CARD_PROGRAMS, FLIP_GIVE } from '@lib/constants';
import TTDPixel from '@modules/common/components/yelp-pixel/yelp-pixel';
import { AutocompleteGoogle } from '@modules/common/components/search-location-input/AutocompleteGoogle';
import { getRedemptionCardInfo } from '@lib/data/order-confirm-handle';
import { getAffiliateByRef } from '@lib/data';
import { AffiliateLandingInterface } from 'types/affiliate';
import { useSearchService } from '@lib/context/search-service-context';
import { SearchService } from '@lib/constants/variants';
import { configurationPageServiceCardProgram } from '@lib/constants/gift-card-type-constants';
import { getReferral, setAwc, setFGToken, setReferral } from '@lib/util/local-storage';
import { ProfileIcon } from '@modules/common/icons/profile';
import RotatingList from '@modules/common/components/routing-listing/rotating-list';
import { geoLocationAsync } from '@lib/data/geo-location-handle';
import { SearchServiceLogo } from '@modules/search-service-logo';
import { useApi } from '@lib/hooks/use-api';

declare let window: any;
const PLACEHOLDER_SEARCH = 'Search for restaurants, spas, and salons';
const PLACEHOLDER_SPAS_AND_SALON = 'Search for a spa or salon';
const PLACEHOLDER_RESTAURANT = 'Search for a restaurant';
const PLACE_HOLDER_SEARCH_ONE_LINE = 'SEARCH FOR A FOOD TYPE, RESTAURANT, OR SALON';
const ERROR_MESSAGE = 'Please enter a location';
const DEFAULT_NUMBER_LOCATION = 5;
const RESTAURANT_NUMBER_LOCATION = 3;
const SPAS_SALONS_NUMBER_LOCATION = 2;
const DEFAULT_LOCATION = 'restaurants, spas, and salons';
const RESTAURANT_LOCATION = 'local restaurants';
const SPAS_SALONS_LOCATION = 'local spas and salons';
const PLACEHOLDER_SEARCH_SERVICE = 'Search for a service provider';
const HEADER_DEFAULT = `Choose from over <span class="text-pink_drink font-bold">5 million</span> restaurants, spas, and salons`;
const HEADER_RESTAURANT = `Choose from over <span class="text-pink_drink font-bold">2 million</span> local restaurants`;
const HEADER_SPA = `Choose from over <span class="text-pink_drink font-bold">3 million</span> local spas and salons`;
const HEADER_SERVICE = `Choose from over <span class="text-pink_drink font-bold">7 million</span> local service providers`;

const Header = () => {
  const api = useApi();
  const searchService = useSearchService();
  const { customer, retrievingCustomer } = useAccount();
  const { setLoading, setRef } = useStore();
  const router = useRouter();
  const pathname = usePathname();
  const termRef = useRef<HTMLInputElement>(null);
  const locationRef = useRef<HTMLInputElement>(null);
  const categoryRef = useRef<HTMLInputElement>(null);
  const [_hasLocation, setHasLocation] = useState(false);
  const [placeHolderSearch, setPlaceHolderSearch] = useState(PLACEHOLDER_SEARCH);
  const [error, setError] = useState<string | null>(null);
  const [enableSearch, setEnableSearch] = useState<boolean>(true);
  const [businessLink, setBusinessLink] = useState<string>('/');
  const cartContext = useContext(CartDropdownContext);
  const [hideSearchLocation, setHideSearchLocation] = useState(false);
  const [hasMRA, setHasMRA] = useState(false);
  const [numberLocation, setNumberLocation] = useState(DEFAULT_NUMBER_LOCATION);
  const [descriptionLocation, setdescriptionLocation] = useState(DEFAULT_LOCATION);
  const [chooseYourOwn, setChooseYourOwn] = useState<string>('');
  const [affiliateByRef, setAffiliateByRef] = useState<AffiliateLandingInterface>();
  const [geoLocation, setGeoLocation] = useState('');
  const [geoLocationDisplay, setGeoLocationDisplay] = useState('');
  const [hasGeoLocation, setHasGeoLocation] = useState(false);
  const currentLocationString = 'Current Location';
  const [calledGeoLocation, setCalledGeoLocation] = useState(false);
  const [searchText, setSearchText] = useState<any>(null);

  const handleGeoLocation = () => {
    if (!navigator.geolocation) {
      // Geolocation API not supported
      setGeoLocation('');
      setHasGeoLocation(false);
      return;
    }

    navigator.permissions.query({ name: 'geolocation' as PermissionName }).then(function (result) {
      if (result.state === 'granted' || result.state === 'prompt') {
        requestLocation();
      } else {
        logger.debug('Geolocation not authorized, waiting for user allowance');
        setGeoLocation('');
        setHasGeoLocation(false);
      }
    });
  };

  const requestLocation = () => {
    navigator.geolocation.getCurrentPosition(successCallback, errorCallback, {
      //NOSONAR not used in secure contexts
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0,
    });
  };

  const successCallback = async (position: GeolocationPosition) => {
    const { latitude, longitude } = position.coords;
    const response = await geoLocationAsync(latitude, longitude);
    if (response?.ok && response.body?.city && response.body?.subdivision) {
      const location = `${response.body.city}, ${response.body.subdivision}`;
      setGeoLocationDisplay(currentLocationString);
      setGeoLocation(location);
      setHasGeoLocation(!!location);
    } else {
      setHasGeoLocation(false);
    }
  };

  const errorCallback = (error: any) => {
    logger.error('Geolocation error:', error);
    setHasGeoLocation(false);
  };

  TTDPixel();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const referrer = urlParams.get('ref');
    const fgToken = urlParams.get('fgtoken');
    const search = urlParams.get('search') ?? '';
    const awc = urlParams.get('awc');

    setReferral(referrer);
    setFGToken(fgToken);
    setAwc(awc);

    if (referrer === FLIP_GIVE && configurationPageServiceCardProgram[search]) {
      router.push(`/businesses/let-them-choose?fgtoken=${fgToken}&ref=flip&search=${search}`);
    }
  }, [router]);

  const {
    location: locationParam,
    term: termParam,
    category: categoryParam,
    search: searchFor,
    gift_card_id,
    provisioned_card_id,
    ref,
    awc,
  } = useSearchHandle();

  const showSectionSearch = useMemo(() => {
    if (
      pathname !== routes.checkout &&
      pathname.indexOf(routes.orderConfirm) === -1 &&
      pathname.indexOf(routes.redemption) === -1 &&
      pathname.indexOf(routes.merchantInquiry) === -1 &&
      pathname.indexOf(routes.accountLogin) === -1 &&
      pathname.indexOf(routes.temporarily_unavailable) === -1 &&
      pathname.indexOf(routes.store_closed) === -1 &&
      pathname.indexOf(routes.profile) === -1 &&
      pathname.indexOf(routes.cart) === -1 &&
      pathname.indexOf('contact-us') === -1 &&
      pathname.indexOf('about') === -1 &&
      pathname.indexOf('faqs') === -1 &&
      pathname.indexOf('privacy-policy') === -1 &&
      pathname.indexOf('terms-and-conditions') === -1 &&
      pathname.indexOf('cardholder-agreement') === -1 &&
      pathname.indexOf('communications-agreement') === -1 &&
      pathname.indexOf('business-contact') === -1 &&
      pathname.indexOf(routes.account) === -1
    ) {
      return true;
    }
    return false;
  }, [pathname]);

  useEffect(() => {
    if (showSectionSearch) {
      window.document.body.classList.add('hasHeaderSearch');
    } else {
      window.document.body.classList.remove('hasHeaderSearch');
    }
  }, [showSectionSearch]);

  useEffect(() => {
    if (_hasLocation) {
      window.document.body.classList.add('hasLocation');
    } else {
      window.document.body.classList.remove('hasLocation');
    }
  }, [_hasLocation]);

  const hasHeaderWithoutSearch = useMemo(() => {
    if (pathname.indexOf(routes.redemption) !== -1 && pathname.endsWith('preview')) {
      return true;
    }
    return false;
  }, [pathname]);

  const hasHeaderWithoutMenu = useMemo(() => {
    if (
      (pathname.indexOf(routes.redemption) !== -1 && !pathname.endsWith('preview')) ||
      pathname.indexOf(routes.temporarily_unavailable) !== -1 ||
      pathname.indexOf(routes.store_closed) !== -1 ||
      pathname.indexOf(routes.merchantInquiry) !== -1 ||
      pathname.indexOf(routes.accountLogin) !== -1
    ) {
      return false;
    }
    return true;
  }, [pathname]);

  const showSectionCartDropdown = useMemo(() => {
    if (pathname.indexOf(routes.redemption) === -1 && pathname.indexOf(routes.merchantInquiry) === -1) {
      return true;
    }
    return false;
  }, [pathname]);

  const showSectionIconUser = useMemo(() => {
    if (
      pathname.indexOf(routes.accountLogin) === -1 &&
      pathname.indexOf(routes.redemption) === -1 &&
      pathname.indexOf(routes.switchMerchant) === -1 &&
      pathname.indexOf(routes.temporarily_unavailable) === -1 &&
      pathname.indexOf(routes.store_closed) === -1
    ) {
      return true;
    }
    return false;
  }, [pathname]);

  const showGiveLocalGiftDescription = useMemo(() => {
    if (pathname.indexOf(routes.search) === -1 && pathname.indexOf(routes.businesses) === -1) {
      return true;
    }
    return false;
  }, [pathname]);

  const hasLoggin = useMemo(() => {
    return !retrievingCustomer && customer;
  }, [customer, retrievingCustomer]);

  const getSearchCategory = (searchBy: string | undefined) => {
    const temp = ('' + searchBy).toLowerCase().replace('search for a', '').trim();
    switch (temp) {
      case 'restaurant':
        return 'restaurant';
      case 'spa or salon':
        return 'spa';
    }
    return '';
  };

  const getSearchPlaceHolder = () => {
    if (hideSearchLocation) return PLACE_HOLDER_SEARCH_ONE_LINE;

    const programValue = sessionStorage.getItem(CARD_PROGRAMS.KEY) ?? categoryParam;
    switch (programValue) {
      case CARD_PROGRAMS.RESTAURANT:
      case CARD_PROGRAMS.CHOOSE_YOUR_OWN_RESTAURANT:
        return PLACEHOLDER_RESTAURANT;
      case CARD_PROGRAMS.SPAS_AND_SALON:
      case CARD_PROGRAMS.CHOOSE_YOUR_OWN_SPAS_SALONS:
        return PLACEHOLDER_SPAS_AND_SALON;
      case CARD_PROGRAMS.CHOOSE_YOUR_OWN_SERVICES:
      case CARD_PROGRAMS.SERVICEPROVIDER:
        return PLACEHOLDER_SEARCH_SERVICE;
      default:
        return PLACEHOLDER_SEARCH;
    }
  };

  const handleSearch = (event?: any) => {
    event?.preventDefault();
    setError(null);
    if (!locationRef.current?.value) {
      return setError(ERROR_MESSAGE);
    }
    if (categoryRef?.current?.value) {
      const url = decodeURIComponent(location.href);
      if (!url.includes('&category=') && !url.includes('?search=')) {
        categoryRef.current.value = '';
      }
    }
    const searchParamsURL = new URLSearchParams(window.location.search);
    let category = searchParamsURL?.get('category');
    if (categoryRef?.current?.value) {
      category = categoryRef?.current?.value;
    }
    const params = {
      location:
        hasGeoLocation && locationRef.current?.value === currentLocationString
          ? geoLocation
          : locationRef.current?.value,
      term: termRef.current?.value,
      category: category ?? '',
      enableSearch: enableSearch,
      sort_by: 'best_match',
    };

    const filteredParams: Record<string, any> = Object.fromEntries(
      Object.entries(params).filter(([_key, value]) => value !== '')
    );
    let urlSearch = `${routes.search}?${objectToURLSearchParams(filteredParams)}`;
    if (pathname === routes.switchMerchant) {
      let chooseYourOwnCategory = '';
      if (chooseYourOwn) {
        chooseYourOwnCategory =
          chooseYourOwn === CARD_PROGRAMS.CHOOSE_YOUR_OWN_RESTAURANT
            ? CARD_PROGRAM_DISPLAY.RESTAURANT
            : CARD_PROGRAM_DISPLAY.SPAS_AND_SALON;
        params.category = chooseYourOwnCategory;
      }
      urlSearch = `${
        routes.switchMerchant
      }?gift_card_id=${gift_card_id}&provisioned_card_id=${provisioned_card_id}&${objectToURLSearchParams(
        filteredParams
      )}`;
    }
    if (hasMRA) {
      urlSearch += `&ref=${affiliateByRef?.referrer_tag}`;
      const card_types = JSON.parse(affiliateByRef?.card_types ?? '');

      if (card_types?.length == 1 && card_types[0] == CARD_PROGRAMS.SPAS_AND_SALON) {
        urlSearch += `&category=${CARD_PROGRAM_DISPLAY.SPAS_AND_SALON}`;
      }
    }
    const termString = termParam ?? '';
    if (locationParam !== params?.location || termString !== params?.term) {
      setLoading(true);
    }
    if (searchService?.currentSearch) {
      urlSearch += `&providerId=${searchService?.currentSearch}`;
    }
    router.push(urlSearch);
  };

  useEffect(() => {
    setPlaceHolderSearch(getSearchPlaceHolder);
    if (locationRef.current && !locationParam) {
      locationRef.current.value = '';
    } else if (locationRef.current && locationParam && locationRef.current.value !== locationParam) {
      locationRef.current.value = locationParam;
    }
    if (termRef.current && !termParam) {
      termRef.current.value = '';
    } else if (termRef.current && termParam && termRef.current.value !== termParam) {
      termRef.current.value = termParam;
    }
  }, [locationParam, termParam, categoryParam]);

  useEffect(() => {
    const getTransactionDetails = async () => {
      let response = await getRedemptionCardInfo(provisioned_card_id as string);
      if (response?.card_program) {
        switch (response.card_program) {
          case CARD_PROGRAMS.CHOOSE_YOUR_OWN_RESTAURANT:
          case CARD_PROGRAMS.RESTAURANT:
            setPlaceHolderSearch(PLACEHOLDER_RESTAURANT);
            setNumberLocation(RESTAURANT_NUMBER_LOCATION);
            setdescriptionLocation(RESTAURANT_LOCATION);
            setChooseYourOwn(CARD_PROGRAMS.CHOOSE_YOUR_OWN_RESTAURANT);
            break;
          case CARD_PROGRAMS.CHOOSE_YOUR_OWN_SPAS_SALONS:
          case CARD_PROGRAMS.SPAS_AND_SALON:
            setPlaceHolderSearch(PLACEHOLDER_SPAS_AND_SALON);
            setNumberLocation(SPAS_SALONS_NUMBER_LOCATION);
            setdescriptionLocation(SPAS_SALONS_LOCATION);
            setChooseYourOwn(CARD_PROGRAMS.CHOOSE_YOUR_OWN_SPAS_SALONS);
            break;
          case CARD_PROGRAMS.SERVICEPROVIDER:
            setPlaceHolderSearch(PLACEHOLDER_SEARCH_SERVICE);
            break;
          default:
            setChooseYourOwn('');
            break;
        }
      } else {
        setChooseYourOwn('');
      }
    };
    if (pathname === routes.switchMerchant) {
      getTransactionDetails();
    }
    setError(null);
  }, [pathname]);

  useEffect(() => {
    setHideSearchLocation(pathname.indexOf(routes.businesses) !== -1);
  }, [pathname]);

  useEffect(() => {
    setPlaceHolderSearch(getSearchPlaceHolder);
  }, [hideSearchLocation]);

  useEffect(() => {
    const currentDomain = window.location.hostname;
    if (currentDomain === domains.dev) {
      setBusinessLink(domains.b2b_dev);
    } else if (currentDomain === domains.qa) {
      setBusinessLink(domains.b2b_qa);
    } else {
      setBusinessLink(domains.prod);
    }

    getGeoLocation();
  }, []);

  const getGeoLocation = () => {
    if (calledGeoLocation) return;

    setTimeout(() => {
      handleGeoLocation();
    }, 1000);

    setCalledGeoLocation(true);
  };

  useEffect(() => {
    if (termRef?.current) {
      setPlaceHolderSearch(getSearchPlaceHolder);
    }

    if (!searchFor) return;
    const category = getSearchCategory(searchFor);
    if (categoryRef.current && category && categoryRef.current.value !== category) {
      categoryRef.current.value = category;
    }
  }, [searchFor]);

  useEffect(() => {
    const currentRef = ref ?? getReferral();
    if (currentRef) {
      const getAffiliate = async (currentRef: string) => {
        try {
          const res = await getAffiliateByRef(currentRef);
          if (res?.ok && res?.body) {
            setAffiliateByRef(res?.body);
            setRef(res?.body);
            setHasMRA(true);
          }
        } catch (error) {
          setHasMRA(false);
        }
      };
      getAffiliate(currentRef);
    }

    setAwc(awc);
  }, [ref, pathname, awc]);

  useEffect(() => {
    if (locationRef?.current && hasMRA && affiliateByRef && affiliateByRef.restriction_type === 'city') {
      locationRef.current.value = affiliateByRef?.city_restriction;
    }
    if (affiliateByRef?.card_types) {
      const card_types = JSON.parse(affiliateByRef?.card_types);
      if (card_types?.length > 2) {
        setPlaceHolderSearch(PLACEHOLDER_SEARCH);
      }
      if (card_types?.length == 1) {
        if (card_types[0] === 'restaurant') {
          setPlaceHolderSearch(PLACEHOLDER_RESTAURANT);
        } else {
          setPlaceHolderSearch(PLACEHOLDER_SPAS_AND_SALON);
          if (categoryRef?.current) {
            categoryRef.current.value = 'spa or salon';
          }
        }
      }
    }
  }, [hasMRA, affiliateByRef]);

  useEffect(() => {
    const handleScroll = () => {
      const headerInner = window.document.querySelector('.header-inner');
      if (!headerInner) return;

      if (window.scrollY > 10) {
        headerInner.classList.add('_scrolled');
      } else {
        headerInner.classList.remove('_scrolled');
      }
    };

    // Add scroll event listener
    if (typeof window !== 'undefined') {
      window.addEventListener('scroll', handleScroll);

      // Initial check in case page loads scrolled down
      handleScroll();

      // Clean up event listener when component unmounts
      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  const handleToHome = () => {
    router.push('/');
  };

  if (hasHeaderWithoutSearch) {
    return (
      <div className="header-withoutSearch">
        <div className="header-withoutSearch-bg"></div>
        <a href="/">
          <MainLogo className="header-logo" />
        </a>
      </div>
    );
  }

  const handleKeyDown = (event?: any) => {
    if (event.key === 'Enter') {
      event.preventDefault(); // Prevent default Enter key behavior
      handleSearch(event); // Trigger the form submission
    }
  };

  const handleClick = (event?: any) => {
    if (!hideSearchLocation) return;
    sessionStorage.setItem(CARD_PROGRAMS.KEY, CARD_PROGRAMS.CHOOSE_YOUR_OWN);
    setHideSearchLocation(false);
  };

  const getHeader = () => {
    if (pathname === routes.switchMerchant) {
      if (categoryParam === CARD_PROGRAMS.CHOOSE_YOUR_OWN_RESTAURANT || categoryParam === CARD_PROGRAMS.RESTAURANT) {
        return HEADER_RESTAURANT;
      }
      if (
        categoryParam === CARD_PROGRAMS.CHOOSE_YOUR_OWN_SPAS_SALONS ||
        categoryParam === CARD_PROGRAMS.SPAS_AND_SALON
      ) {
        return HEADER_SPA;
      }
      if (categoryParam === CARD_PROGRAMS.CHOOSE_YOUR_OWN_SERVICES || categoryParam === CARD_PROGRAMS.SERVICEPROVIDER) {
        return HEADER_SERVICE;
      }
    }
    return HEADER_DEFAULT;
  };

  const handleFormSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    handleSearch();
  };

  const handleLocationSelect = (location: string) => {
    setSearchText(null);
    if (locationRef.current) {
      locationRef.current.value = location;
    }
    handleSearch();
  };
  const presentationItems = [
    {
      content: `Do they love burgers? 🍔 Search for their <strong><em>favorite</em></strong> below. <em>Bet we have it!</em>`,
      key: 'smash burgers',
      label: '(thinking <u>top smash burger joints</u>?)',
    },
    {
      content: `You’ve got <strong><em>great taste</em></strong>. We’ve got gift cards to match.`,
      key: 'best lunch spots',
      label: '(thinking <u>best lunch spots near me</u>?)',
    },
    {
      content: `Foodie-level gifts? 🍽️ Yeah, <strong><em>we’re ready</em></strong> for that challenge.`,
      key: 'chef-owned restaurants',
      label: '(thinking <u>top chef-owned restaurants</u>?)',
    },
    {
      content: `You’d gift a taco truck? <strong><em>Respect.</em></strong> 🌮 We got ‘em.`,
      key: 'local taquerias',
      label: '(thinking <u>local taquerias</u>?)',
    },
  ];

  return (
    <div className={clsx('headerPage inset-x-0 z-[9999] group bg-light_gray md:fixed md:top-0 md:z-50')}>
      <header className={'header-inner relative z-[9999]'}>
        <div className="headerTop-mobile">
          <figure className="_brand">
            <div className="flex w-full justify-center items-center">
              {hasMRA && (
                <div className="mra-logo">
                  <Link
                    href={affiliateByRef?.logo_redirect ?? '#'}
                    prefetch={false}
                    target={affiliateByRef?.logo_redirect ? '_blank' : ''}
                  >
                    <img
                      src={affiliateByRef?.logo ?? ''}
                      alt={affiliateByRef?.referrer_tag ?? ''}
                      width="87"
                      height={'120'}
                      className="mt-2"
                    />
                  </Link>
                </div>
              )}
              {pathname !== routes.temporarily_unavailable &&
              pathname !== routes.store_closed &&
              pathname !== routes.home ? (
                <Link
                  href="/"
                  prefetch={false}
                  className={clsx({
                    'header-logo-margin': showSectionIconUser && showSectionCartDropdown,
                  })}
                  onClick={() => {
                    sessionStorage.removeItem(CARD_PROGRAMS.KEY);
                  }}
                >
                  <MainLogo className="header-logo" />
                </Link>
              ) : (
                <div className={clsx({ 'header-logo-margin': false })} onClick={handleToHome}>
                  <MainLogo className="header-logo" />
                </div>
              )}
            </div>
          </figure>
        </div>
        {showSectionSearch && (
          <RotatingList items={presentationItems} interval={4000} className={'_prenav'} onChange={setSearchText} />
        )}
        <div className="header-main">
          <div
            className={clsx('_brand-desktop flex items-center flex-none md:w-[186px]', {
              'w-[77px]': showSectionIconUser,
            })}
          >
            <figure className="_brand desktop">
              <div className="flex w-full justify-center items-center">
                {hasMRA && (
                  <div className="mra-logo">
                    <Link
                      href={affiliateByRef?.logo_redirect ?? '#'}
                      prefetch={false}
                      target={affiliateByRef?.logo_redirect ? '_blank' : ''}
                    >
                      <img
                        src={affiliateByRef?.logo ?? ''}
                        alt={affiliateByRef?.referrer_tag ?? ''}
                        width="87"
                        height={'120'}
                        className="mt-2"
                      />
                    </Link>
                  </div>
                )}
                {pathname !== routes.temporarily_unavailable &&
                pathname !== routes.store_closed &&
                pathname !== routes.home ? (
                  <Link
                    href="/"
                    prefetch={false}
                    className={clsx({
                      'header-logo-margin': showSectionIconUser && showSectionCartDropdown,
                    })}
                    onClick={() => {
                      sessionStorage.removeItem(CARD_PROGRAMS.KEY);
                    }}
                  >
                    <MainLogo className="header-logo" />
                  </Link>
                ) : (
                  <div className={clsx({ 'header-logo-margin': false })} onClick={handleToHome}>
                    <MainLogo className="header-logo" />
                  </div>
                )}
              </div>
            </figure>
          </div>
          {showSectionSearch && (
            <div className={`_navsearch ${_hasLocation ? 'open' : ''}`}>
              {showSectionSearch && (
                <>
                  <div className="giveLocalGift hidden">
                    {showGiveLocalGiftDescription && (
                      <div className="giveLocalGift-description">
                        {hasMRA ? (
                          <>{affiliateByRef?.description}</>
                        ) : (
                          <>
                            <p
                              dangerouslySetInnerHTML={{
                                __html: getHeader(),
                              }}
                            ></p>
                          </>
                        )}
                      </div>
                    )}
                    {hasMRA && (
                      <button
                        type="button"
                        className="font-semibold mra-link"
                        onClick={() => window.open(`${affiliateByRef?.url}`, '_blank', 'noopener,noreferrer')}
                      >
                        {affiliateByRef?.button_text}
                      </button>
                    )}
                  </div>
                  <div>
                    <div className="">
                      <form onSubmit={handleFormSubmit} role="search">
                        <div className={`header-search ${_hasLocation ? 'open' : ''}`} onClick={handleClick}>
                          <div className="header-input relative">
                            <button
                              type="button"
                              className="header-inputIcon"
                              onClick={(e) => termRef.current?.focus()}
                              aria-label="button search icon"
                            >
                              <SearchIcon size={20} />
                            </button>
                            <label htmlFor="search_business" aria-label="Business Search" className="hidden">
                              Business Search:
                            </label>
                            <input
                              id="headerSearch"
                              aria-label="search business"
                              name="search_business"
                              type="search"
                              ref={termRef}
                              placeholder={'Search'}
                              onFocus={() => setHasLocation(true)}
                              onKeyDown={handleKeyDown}
                              autoComplete="off"
                              onBlur={() => {
                                setTimeout(() => {
                                  if (!window.document.querySelector('.header-search:hover')) {
                                    setHasLocation(false);
                                  }
                                }, 200);
                              }}
                            />
                            <input
                              aria-label="search business category hidden"
                              id="headerCategory"
                              type="hidden"
                              ref={categoryRef}
                              autoComplete="off"
                            />
                            {!termRef.current?.value && (
                              <button
                                type="button"
                                className="btn-search-text"
                                onClick={(e) => {
                                  if (termRef.current) {
                                    termRef.current.value = searchText?.key;
                                    setSearchText(null);
                                    handleSearch(e);
                                  }
                                }}
                                aria-label="button close icon"
                                dangerouslySetInnerHTML={{ __html: searchText?.label }}
                              />
                            )}
                          </div>
                          {!hideSearchLocation && (
                            <div
                              className={clsx('header-input header-input-location header-input--isBorder', {
                                '!hidden': affiliateByRef?.restriction_type === 'city',
                              })}
                            >
                              <span className="header-inputIcon"></span>
                              {!hasMRA ? (
                                <SearchLocationInput
                                  ref={locationRef}
                                  onSearch={handleLocationSelect}
                                  setEnableSearch={setEnableSearch}
                                  hasMRA={hasMRA}
                                  defaultValue={locationParam || ''}
                                  locationDisplay={geoLocationDisplay}
                                />
                              ) : (
                                <AutocompleteGoogle
                                  ref={locationRef}
                                  onSearch={() => handleLocationSelect('')}
                                  setEnableSearch={setEnableSearch}
                                  hasMRA={hasMRA}
                                  affiliateByRef={affiliateByRef}
                                  defaultValue={locationParam || ''}
                                />
                              )}
                            </div>
                          )}
                          <button
                            type="button"
                            onClick={(e) => {
                              handleSearch(e);
                            }}
                            aria-label="button geolocation icon"
                            className="btn-search bg-[#ff1a1a] py-1.5 px-2.5 rounded-full font-semibold text-white transition"
                          >
                            <SearchIcon size={20} />
                            <span>Search</span>
                          </button>
                        </div>
                        {error && <ErrorMessage className="!text-sm mt-1">{error}</ErrorMessage>}
                        <div className="flex justify-between mt-2.5">
                          <div className="text-sm text-gray-700 flex justify-center mt-1 geo-location">
                            {hasGeoLocation && (
                              <div className="geo-node">
                                <div className="geo-location-icon">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="13.51"
                                    height="13.51"
                                    viewBox="0 0 13.51 13.51"
                                  >
                                    <path d="M12.94.59c-.54-.54-1.28-.74-2.03-.47L1.38 3.3C.36 3.64-.24 4.72.09 5.73c.2.54.61 1.01 1.15 1.22L4.76 8.3c.14.07.27.2.34.34l1.35 3.58c.27.74 1.01 1.28 1.83 1.28h.07c.81 0 1.56-.54 1.83-1.35l3.18-9.6c.34-.68.14-1.49-.41-1.96Z"></path>
                                  </svg>
                                </div>
                                <div className="text-xs md:text-sm">
                                  It looks like you're in <span>{geoLocation}</span>. Not correct? Specify your location
                                  above!
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="giveLocalGift-yelp">
                            <SearchServiceLogo />
                          </div>
                        </div>
                        <button type="submit" className="hidden" aria-label="submit" />
                      </form>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
          <div
            className={clsx('_navRight', {
              'w-[115px]': showSectionIconUser,
            })}
          >
            {
              <div
                className={clsx('mt-0 h-7', {
                  '': showSectionIconUser,
                })}
              >
                <CartDropdown />
              </div>
            }
            {cartContext?.pointMode ? (
              <div className="mt-10 text-pink_drink select-none">
                <div className="w-28 border border-pink_drink rounded-2xl py-2 text-center bg-off_white font-medium">
                  <div>Total Points:</div>
                  <div>{cartContext.totalPoints.toLocaleString()} pts</div>
                </div>
              </div>
            ) : (
              <>
                {showSectionIconUser && (
                  <div
                    className={clsx('flex items-center', {
                      'w-5': !showSectionCartDropdown,
                    })}
                  >
                    {hasLoggin && (
                      <Link
                        href={routes.profile}
                        prefetch={false}
                        className="mr-1 object-cover"
                        aria-label="link user icon"
                      >
                        <User size={'1.8em'} />
                      </Link>
                    )}
                    {!hasLoggin && (
                      <>
                        {api.disableLoginSignup ? (
                          <div className="lg:w-[200px] md:w-[100px] sm:w-[50px]"></div>
                        ) : (
                          <div className="flex items-center">
                            <div className="flex-auto h-[28px]">
                              <Link
                                href={`${routes.accountLogin}?view=${LOGIN_VIEW.SIGN_IN}`}
                                prefetch={false}
                                className="mr-1"
                              >
                                <ProfileIcon />
                              </Link>
                              <Link
                                href={routes.accountLogin}
                                prefetch={false}
                                className="mr-1 object-cover userNav-icon !hidden"
                                aria-label="link user icon"
                              >
                                <User size={'1.8em'} />
                              </Link>
                            </div>
                            <div className="flex-auto sign-up-btn !hidden">
                              <Link
                                href={`${routes.accountLogin}?view=${LOGIN_VIEW.REGISTER}`}
                                prefetch={false}
                                className="userNav-btn userNav-btn--isPrimary"
                              >
                                SIGN UP
                              </Link>
                            </div>
                          </div>
                        )}
                      </>
                    )}
                    {
                      <div
                        className={clsx('mt-0 h-7 hidden', {
                          'ml-2': showSectionIconUser,
                        })}
                      >
                        <CartDropdown />
                      </div>
                    }
                  </div>
                )}
              </>
            )}
            {hasHeaderWithoutMenu && (
              <>
                <Menu as="div">
                  {({ open }) => (
                    <>
                      <div className="h-[16px]">
                        <Menu.Button aria-label="button menu icon" className={`_burger ${open ? '-active' : ''}`}>
                          <span>MENU</span>
                        </Menu.Button>
                      </div>
                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <Menu.Items className="focus-visible::outline-0 focus:outline-0 outline-none z-2000">
                          <div className="navbar focus-visible::outline-0">
                            <Menu.Item>
                              <Link className="navbar-item navbar-item-bgGray" href="/about" prefetch={false}>
                                About Us
                              </Link>
                            </Menu.Item>
                            <Menu.Item>
                              <Link className="navbar-item" href="/contact-us" prefetch={false}>
                                Contact Us
                              </Link>
                            </Menu.Item>
                            <Menu.Item>
                              <Link
                                href={businessLink + `/contact`}
                                prefetch={false}
                                className="navbar-item navbar-item-bgGray"
                              >
                                For Business
                              </Link>
                            </Menu.Item>
                            <Menu.Item>
                              <Link
                                className="navbar-item"
                                href="https://app.akimbocard.com/app/#/akimbo/cardBalance"
                                prefetch={false}
                                target="_blank"
                              >
                                Check Balance
                              </Link>
                            </Menu.Item>
                            <Menu.Item>
                              <Link className="navbar-item navbar-item-bgGray" href="/faqs" prefetch={false}>
                                FAQs
                              </Link>
                            </Menu.Item>
                            {searchService.hasGoogle && searchService.hasYelp && (
                              <>
                                {searchService?.currentSearch == SearchService.YELP && (
                                  <Menu.Item>
                                    <button
                                      className="text-left navbar-item border-b border-[#bbb9b9]"
                                      onClick={() => searchService.updateCurrentSearch(SearchService.GOOGLE)}
                                      type="button"
                                    >
                                      Use Google Search
                                    </button>
                                  </Menu.Item>
                                )}
                                {searchService?.currentSearch == SearchService.GOOGLE && (
                                  <Menu.Item>
                                    <button
                                      className="text-left navbar-item border-b border-[#bbb9b9]"
                                      onClick={() => searchService.updateCurrentSearch(SearchService.YELP)}
                                      type="button"
                                    >
                                      Use Yelp Search
                                    </button>
                                  </Menu.Item>
                                )}
                              </>
                            )}
                          </div>
                        </Menu.Items>
                      </Transition>
                    </>
                  )}
                </Menu>
                {hasMRA && (
                  <button
                    type="button"
                    className="mra-btn"
                    style={{
                      backgroundColor: '#' + affiliateByRef?.button_color,
                      color: '#' + affiliateByRef?.text_color,
                    }}
                    onClick={() => window.open(`${affiliateByRef?.url}`, '_blank', 'noopener,noreferrer')}
                  >
                    {affiliateByRef?.button_text}
                  </button>
                )}
              </>
            )}
          </div>
          {error && <ErrorMessage className="!text-base mt-2 hidden">{error}</ErrorMessage>}
        </div>
      </header>
    </div>
  );
};

export default Header;
